import type { Role } from '@/lib/api/types';

/**
 * RBAC matrix — mirrors docs/api/_shared/conventions.md and backend rbac.go exactly.
 * Used for menu visibility + control enablement ONLY; the server is authoritative and
 * every 403 is handled defensively (plan §4.3 / spec §4).
 */
export type Permission =
  | 'content:read'
  | 'content:write'
  | 'content:publish'
  | 'content:delete'
  | 'content:translate'
  | 'media:manage'
  | 'leads:read'
  | 'leads:manage'
  | 'users:manage'
  | 'settings:manage'
  | 'audit:read';

const ALL: Permission[] = [
  'content:read',
  'content:write',
  'content:publish',
  'content:delete',
  'content:translate',
  'media:manage',
  'leads:read',
  'leads:manage',
  'users:manage',
  'settings:manage',
  'audit:read',
];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: ALL,
  editor: [
    'content:read',
    'content:write',
    'content:publish',
    'content:translate',
    'media:manage',
    'leads:read',
    'leads:manage',
    'audit:read',
  ],
  author: ['content:read', 'content:write', 'content:translate', 'media:manage'],
  translator: ['content:read', 'content:translate'],
  sales: ['leads:read', 'leads:manage'],
  viewer: ['content:read', 'leads:read', 'audit:read'],
};

export function roleHas(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/** Human-readable label for tooltips on disabled controls (spec §4 rule of thumb). */
export const PERMISSION_LABELS: Record<Permission, string> = {
  'content:read': 'view content',
  'content:write': 'create or edit content',
  'content:publish': 'publish, schedule, or unpublish',
  'content:delete': 'delete content',
  'content:translate': 'edit translations',
  'media:manage': 'manage media',
  'leads:read': 'view leads',
  'leads:manage': 'manage leads',
  'users:manage': 'administer users',
  'settings:manage': 'manage site settings',
  'audit:read': 'view the audit log',
};
